import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, X, ChevronDown, ChevronUp } from "lucide-react";
import { useFoundationalTaskTags } from "@/hooks/useFoundationalTaskTags";

interface TagSelectorProps {
  brandId: string;
  selectedTagIds: string[];
  onTagsChange: (tagIds: string[]) => void;
}

const TAG_COLORS = [
  '#ef4444', '#f97316', '#f59e0b', '#eab308', 
  '#84cc16', '#22c55e', '#10b981', '#14b8a6', 
  '#06b6d4', '#0ea5e9', '#3b82f6', '#6366f1', 
  '#8b5cf6', '#a855f7', '#d946ef', '#ec4899'
];

export const TagSelector = ({ brandId, selectedTagIds, onTagsChange }: TagSelectorProps) => {
  const { tags, createTag } = useFoundationalTaskTags(brandId);
  const [showSelector, setShowSelector] = useState(false);
  const [showCreateNew, setShowCreateNew] = useState(false);
  const [newTagName, setNewTagName] = useState("");
  const [selectedColor, setSelectedColor] = useState(TAG_COLORS[0]);

  const selectedTags = tags.filter(tag => selectedTagIds.includes(tag.id));
  const availableTags = tags.filter(tag => !selectedTagIds.includes(tag.id));

  const handleAddTag = (tagId: string) => {
    onTagsChange([...selectedTagIds, tagId]);
  };

  const handleRemoveTag = (tagId: string) => {
    onTagsChange(selectedTagIds.filter(id => id !== tagId));
  };

  const handleCreateTag = async () => {
    if (!newTagName.trim()) return;

    await createTag.mutateAsync({
      name: newTagName.trim(),
      color: selectedColor,
    });

    setNewTagName("");
    setSelectedColor(TAG_COLORS[0]);
    setShowCreateNew(false);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 py-2 px-3 border rounded-lg bg-muted/30">
        <div className="flex flex-wrap gap-1.5 flex-1">
          {selectedTags.length === 0 && (
            <span className="text-sm text-muted-foreground">No tags</span>
          )}
          {selectedTags.map(tag => (
            <Badge
              key={tag.id}
              style={{ backgroundColor: tag.color }}
              className="text-white gap-1 pr-1 text-xs"
            >
              {tag.name}
              <button
                onClick={() => handleRemoveTag(tag.id)}
                className="ml-1 hover:bg-white/20 rounded-full p-0.5"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>

        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => setShowSelector(!showSelector)}
          className="h-7 w-7 p-0 shrink-0"
        >
          {showSelector ? <ChevronUp className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
        </Button>
      </div>

      {showSelector && (
        <div className="border rounded-lg p-3 bg-muted/30 space-y-3">
          {!showCreateNew ? (
            <>
              <div className="space-y-2">
                <p className="text-sm font-medium">Select tag</p>
                <div className="flex flex-wrap gap-2 max-h-[200px] overflow-y-auto">
                  {availableTags.map(tag => (
                    <Badge
                      key={tag.id}
                      style={{ backgroundColor: tag.color }}
                      className="text-white cursor-pointer hover:opacity-80"
                      onClick={() => handleAddTag(tag.id)}
                    >
                      {tag.name}
                    </Badge>
                  ))}
                  {availableTags.length === 0 && (
                    <p className="text-sm text-muted-foreground">All tags added</p>
                  )}
                </div>
              </div>

              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowCreateNew(true)}
                className="w-full"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create New Tag
              </Button>
            </>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium">Create new tag</p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowCreateNew(false)}
                  className="h-6 px-2 text-xs"
                >
                  Back
                </Button>
              </div>
              <Input
                placeholder="Tag name"
                value={newTagName}
                onChange={(e) => setNewTagName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleCreateTag();
                  }
                }}
              />
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground">Select color</p>
                <div className="grid grid-cols-8 gap-2">
                  {TAG_COLORS.map(color => (
                    <button
                      key={color}
                      className={`w-6 h-6 rounded-full border-2 ${
                        selectedColor === color ? 'border-foreground' : 'border-transparent'
                      }`}
                      style={{ backgroundColor: color }}
                      onClick={() => setSelectedColor(color)}
                    />
                  ))}
                </div>
              </div>
              <Button
                onClick={handleCreateTag}
                disabled={!newTagName.trim() || createTag.isPending}
                size="sm"
                className="w-full"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Tag
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
